/**
 * Dashboard — landing page summarising the warehouse split by platform.
 *
 * Three platform cards (iOS / Android / Web GA4) and a 30-day daily-
 * downloads line chart comparing iOS vs Android. Powered by
 * `dashboardApi.getPlatformSummary()` from mrdsApi.js.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import LanguageIcon from '@mui/icons-material/Language';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { dashboardApi, refreshCache } from '../services/mrdsApi';
import { PageHeader, SectionCard, EmptyState } from '../components/ui';
import { PLATFORM_COLORS } from '../theme';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));

const Stat = ({ label, value, hint }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

const PlatformCard = ({ title, Icon, color, appCountLabel, appCount, metrics }) => (
  <Card sx={{ height: '100%' }}>
    <Box
      sx={{
        height: 4,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`,
      }}
    />
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            bgcolor: alpha(color, 0.12),
            color,
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {appCountLabel} {fmt(appCount)}
          </Typography>
        </Box>
      </Stack>
      <Grid container spacing={2}>
        {metrics.map((m, i) => (
          <Grid item xs={6} key={i}>
            <Stat {...m} />
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const theme = useTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    dashboardApi
      .getPlatformSummary()
      .then((s) => {
        if (cancelled) return;
        setSummary(s);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Özet veri yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const chartData = (() => {
    if (!summary) return [];
    const map = new Map();
    summary.trend.ios.forEach(({ report_date, downloads }) => {
      const row = map.get(report_date) || { report_date };
      row.ios = downloads;
      map.set(report_date, row);
    });
    summary.trend.android.forEach(({ report_date, downloads }) => {
      const row = map.get(report_date) || { report_date };
      row.android = downloads;
      map.set(report_date, row);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.report_date.localeCompare(b.report_date),
    );
  })();

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Marketing Dashboard"
        subtitle="Per-platform snapshot of downloads, ratings, subscriptions and web traffic. 30-day download trend below."
        rightSlot={
          <MuiTooltip title="Refresh">
            <IconButton
              onClick={() => {
                refreshCache();
                setReloadKey((k) => k + 1);
              }}
            >
              <RefreshIcon />
            </IconButton>
          </MuiTooltip>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading || !summary ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <PlatformCard
                title="iOS"
                Icon={AppleIcon}
                color={PLATFORM_COLORS.ios}
                appCountLabel="Apps"
                appCount={summary.ios.app_count}
                metrics={[
                  { label: 'Lifetime downloads', value: fmt(summary.ios.total_downloads) },
                  {
                    label: 'Avg rating',
                    value: summary.ios.avg_rating != null ? summary.ios.avg_rating.toFixed(2) : '—',
                  },
                  {
                    label: 'Active subscribers',
                    value: fmt(summary.ios.active_subscribers),
                    hint: summary.ios.subs_as_of ? `as of ${summary.ios.subs_as_of}` : null,
                  },
                  { label: 'App count', value: fmt(summary.ios.app_count) },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PlatformCard
                title="Android"
                Icon={AndroidIcon}
                color={PLATFORM_COLORS.android}
                appCountLabel="Apps"
                appCount={summary.android.app_count}
                metrics={[
                  { label: 'Lifetime downloads', value: fmt(summary.android.total_downloads) },
                  {
                    label: 'Avg rating',
                    value: summary.android.avg_rating != null ? summary.android.avg_rating.toFixed(2) : '—',
                  },
                  { label: 'Active subscribers', value: fmt(summary.android.active_subscribers) },
                  { label: 'App count', value: fmt(summary.android.app_count) },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PlatformCard
                title="Web (GA4)"
                Icon={LanguageIcon}
                color={PLATFORM_COLORS.web}
                appCountLabel="Properties"
                appCount={summary.web.app_count}
                metrics={[
                  {
                    label: 'Active users',
                    value: fmt(summary.web.active_users),
                    hint: summary.web.as_of ? `as of ${summary.web.as_of}` : null,
                  },
                  { label: 'New users', value: fmt(summary.web.new_users) },
                  { label: 'Event count', value: fmt(summary.web.event_counts) },
                  { label: 'Property count', value: fmt(summary.web.app_count) },
                ]}
              />
            </Grid>
          </Grid>

          <SectionCard
            title="Last 30 days"
            description="Daily downloads, iOS vs Android"
          >
            {chartData.length === 0 ? (
              <EmptyState title="No download data yet" dense />
            ) : (
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="report_date"
                      stroke={theme.palette.text.secondary}
                      fontSize={12}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      fontSize={12}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[3],
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="ios"
                      name="iOS"
                      stroke={PLATFORM_COLORS.ios}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="android"
                      name="Android"
                      stroke={PLATFORM_COLORS.android}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </SectionCard>
        </>
      )}
    </Container>
  );
}
